import {
  ClientModel,
  clientModelSchema,
  InventoryItemModel,
  inventoryItemModelSchema,
  ProjectInventoryItemModel,
  projectInventoryItemModelSchema,
  ProjectInventoryItemUpdate,
  ProjectInventoryItemUpdateSortOrder,
  ProjectModel,
  projectModelSchema,
  type ClientCreate,
  type ClientUpdate,
  type ProjectCreate,
  type ProjectUpdate,
} from '@itemize/server/models'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import z from 'zod/v4'
import { API_BASE_URL } from '../config.ts'
import { getStoredCredentials, createBasicAuthHeader } from '../libs/auth.ts'

export interface InventoryItemFormData {
  name: string
  description: string
  priceCents: number
  webUrl: string
  image?: File | null
}

const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers) => {
    // Get credentials from sessionStorage
    const credentials = getStoredCredentials()
    if (credentials) {
      const authHeader = createBasicAuthHeader(credentials.username, credentials.password)
      headers.set('Authorization', authHeader)
    }
    return headers
  },
})

// Enhanced base query that handles 401 responses by redirecting to login
const baseQueryWithReauth = async (
  args: Parameters<typeof baseQuery>[0],
  api: Parameters<typeof baseQuery>[1],
  extraOptions: Parameters<typeof baseQuery>[2],
) => {
  let result = await baseQuery(args, api, extraOptions)

  // If we get a 401, redirect to login page
  if (result.error && 'status' in result.error && result.error.status === 401) {
    // Only redirect if we're not already on the login page
    if (window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
  }

  return result
}

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Client', 'Project', 'InventoryItem', 'ProjectInventoryItem'],
  endpoints: (build) => ({
    /**
     * Clients
     */
    getClients: build.query<ClientModel[], void>({
      query: () => '/clients',
      responseSchema: z.array(clientModelSchema),
      providesTags: ['Client'],
    }),
    getClient: build.query<ClientModel, string>({
      query: (id) => `/clients/${id}`,
      responseSchema: clientModelSchema,
      providesTags: (result, error, id) => [{ type: 'Client', id }],
    }),
    createClient: build.mutation<ClientModel, ClientCreate>({
      query: (client) => ({
        url: '/clients',
        method: 'POST',
        body: client,
      }),
      responseSchema: clientModelSchema,
      invalidatesTags: ['Client'],
    }),
    updateClient: build.mutation<ClientModel, { id: string; data: ClientUpdate }>({
      query: ({ id, data }) => ({
        url: `/clients/${id}`,
        method: 'PUT',
        body: data,
      }),
      responseSchema: clientModelSchema,
      invalidatesTags: (result, error, { id }) => [{ type: 'Client', id }, 'Client'],
    }),
    deleteClient: build.mutation<{ message: string; client: ClientModel }, string>({
      query: (id) => ({
        url: `/clients/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Client', id }, 'Client'],
    }),

    /**
     * Projects
     */
    getProjects: build.query<ProjectModel[], void>({
      query: () => '/projects',
      responseSchema: z.array(projectModelSchema),
      providesTags: ['Project'],
    }),
    getProjectsByClient: build.query<ProjectModel[], string>({
      query: (clientId) => `/clients/${clientId}/projects`,
      responseSchema: z.array(projectModelSchema),
      providesTags: (result, error, clientId) => [
        { type: 'Project', id: `client-${clientId}` },
        'Project',
      ],
    }),
    getProject: build.query<ProjectModel, string>({
      query: (id) => `/projects/${id}`,
      responseSchema: projectModelSchema,
      providesTags: (result, error, id) => [{ type: 'Project', id }],
    }),
    createProject: build.mutation<ProjectModel, ProjectCreate>({
      query: (project) => ({
        url: '/projects',
        method: 'POST',
        body: project,
      }),
      responseSchema: projectModelSchema,
      invalidatesTags: (result, error, project) => [
        'Project',
        { type: 'Project', id: `client-${project.clientId}` },
      ],
    }),
    updateProject: build.mutation<ProjectModel, { id: string; data: ProjectUpdate }>({
      query: ({ id, data }) => ({
        url: `/projects/${id}`,
        method: 'PUT',
        body: data,
      }),
      responseSchema: projectModelSchema,
      invalidatesTags: (result, error, { id, data }) => [
        { type: 'Project', id },
        'Project',
        // ...(data.clientId ? [{ type: 'Project' as const, id: `client-${data.clientId}` }] : []),
      ],
    }),
    deleteProject: build.mutation<{ message: string; project: ProjectModel }, string>({
      query: (id) => ({
        url: `/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Project', id },
        'Project',
        ...(result?.project?.clientId
          ? [{ type: 'Project' as const, id: `client-${result.project.clientId}` }]
          : []),
      ],
    }),
    uploadProjectImage: build.mutation<ProjectModel, { projectId: string; image: File }>({
      query: ({ projectId, image }) => {
        const formData = new FormData()
        formData.append('image', image)
        return {
          url: `/projects/${projectId}/image`,
          method: 'POST',
          body: formData,
        }
      },
      responseSchema: projectModelSchema,
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Project', id: projectId },
        'Project',
        ...(result?.clientId
          ? [{ type: 'Project' as const, id: `client-${result.clientId}` }]
          : []),
      ],
    }),
    deleteProjectImage: build.mutation<ProjectModel, string>({
      query: (projectId) => ({
        url: `/projects/${projectId}/image`,
        method: 'DELETE',
      }),
      responseSchema: projectModelSchema,
      invalidatesTags: (result, error, projectId) => [
        { type: 'Project', id: projectId },
        'Project',
        ...(result?.clientId
          ? [{ type: 'Project' as const, id: `client-${result.clientId}` }]
          : []),
      ],
    }),

    /**
     * Project Inventory Items
     */
    getProjectInventoryItems: build.query<ProjectInventoryItemModel[], string>({
      query: (projectId) => `/projects/${projectId}/inventory-items`,
      responseSchema: z.array(projectInventoryItemModelSchema),
      providesTags: (result, error, projectId) => [
        { type: 'ProjectInventoryItem', id: projectId },
        'ProjectInventoryItem',
      ],
    }),
    bulkAddProjectInventoryItems: build.mutation<
      ProjectInventoryItemModel[],
      { projectId: string; items: Array<{ inventoryItemId: string; quantity: number }> }
    >({
      query: ({ projectId, items }) => ({
        url: `/projects/${projectId}/inventory-items/bulk-add`,
        method: 'POST',
        body: { items },
      }),
      responseSchema: z.array(projectInventoryItemModelSchema),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'ProjectInventoryItem', id: projectId },
        'ProjectInventoryItem',
      ],
    }),
    updateProjectInventoryItem: build.mutation<
      ProjectInventoryItemModel,
      { id: string; data: ProjectInventoryItemUpdate }
    >({
      query: ({ id, data }) => ({
        url: `/project-inventory-items/${id}`,
        method: 'PUT',
        body: data,
      }),
      responseSchema: projectInventoryItemModelSchema,
      invalidatesTags: (result, error, { id }) => [
        { type: 'ProjectInventoryItem', id },
        'ProjectInventoryItem',
        ...(result?.projectId
          ? [
              {
                type: 'ProjectInventoryItem' as const,
                id: result.projectId,
              },
            ]
          : []),
      ],
    }),
    deleteProjectInventoryItem: build.mutation<
      { message: string; projectInventoryItem: ProjectInventoryItemModel },
      string
    >({
      query: (id) => ({
        url: `/project-inventory-items/${id}`,
        method: 'DELETE',
      }),
      responseSchema: z.object({
        message: z.string(),
        projectInventoryItem: projectInventoryItemModelSchema,
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ProjectInventoryItem', id },
        'ProjectInventoryItem',
        ...(result?.projectInventoryItem?.projectId
          ? [
              {
                type: 'ProjectInventoryItem' as const,
                id: result.projectInventoryItem.projectId,
              },
            ]
          : []),
      ],
    }),
    updateProjectInventoryItemSortOrder: build.mutation<
      ProjectInventoryItemModel[],
      { id: string; data: ProjectInventoryItemUpdateSortOrder }
    >({
      query: ({ id, data }) => ({
        url: `/project-inventory-items/update-sort-order`,
        method: 'POST',
        body: data,
      }),
      responseSchema: z.array(projectInventoryItemModelSchema),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ProjectInventoryItem', id },
        'ProjectInventoryItem',
      ],
    }),

    /**
     * Inventory Items
     */
    getInventoryItems: build.query<InventoryItemModel[], void>({
      query: () => '/inventory-items',
      responseSchema: z.array(inventoryItemModelSchema),
      providesTags: (result) =>
        result
          ? [
              'InventoryItem',
              ...result.map((item) => ({ type: 'InventoryItem' as const, id: item.id })),
            ]
          : ['InventoryItem'],
    }),
    getInventoryItem: build.query<InventoryItemModel, string>({
      query: (id) => `/inventory-items/${id}`,
      responseSchema: inventoryItemModelSchema,
      providesTags: (result, error, id) => [{ type: 'InventoryItem', id }],
    }),
    createInventoryItem: build.mutation<InventoryItemModel, InventoryItemFormData>({
      query: (data) => {
        const formData = new FormData()
        formData.append('name', data.name)
        formData.append('priceCents', data.priceCents.toString())

        if (data.description && data.description.trim()) {
          formData.append('description', data.description.trim())
        }

        if (data.webUrl && data.webUrl.trim()) {
          formData.append('webUrl', data.webUrl.trim())
        }

        if (data.image) {
          formData.append('image', data.image)
        }
        return {
          url: '/inventory-items',
          method: 'POST',
          body: formData,
        }
      },
      responseSchema: inventoryItemModelSchema,
      invalidatesTags: ['InventoryItem'],
    }),
    updateInventoryItem: build.mutation<
      InventoryItemModel,
      { data: InventoryItemFormData; item: InventoryItemModel | null }
    >({
      query: ({ data, item }) => {
        if (!item) {
          throw new Error('Item not found')
        }
        // Create FormData for multipart upload
        const formData = new FormData()

        // Only include fields that have changed or are required
        if (data.name !== item.name) {
          formData.append('name', data.name)
        }

        if (data.description !== (item.description || '')) {
          formData.append('description', data.description.trim())
        }

        if (data.webUrl !== (item.webUrl || '')) {
          formData.append('webUrl', data.webUrl.trim())
        }

        if (data.priceCents !== item.priceCents) {
          formData.append('priceCents', data.priceCents.toString())
        }

        if (data.image === null) {
          // Empty string signals deletion
          formData.append('image', '')
        } else if (data.image) {
          formData.append('image', data.image)
        }
        return {
          url: `/inventory-items/${item.id}`,
          method: 'PUT',
          body: formData,
        }
      },
      responseSchema: inventoryItemModelSchema,
      invalidatesTags: (result, error, { item }) => [
        { type: 'InventoryItem', id: item!.id },
        'InventoryItem',
      ],
    }),
    deleteInventoryItem: build.mutation<{ message: string; item: InventoryItemModel }, string>({
      query: (id) => ({
        url: `/inventory-items/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'InventoryItem', id }, 'InventoryItem'],
    }),
  }),
})
